import React, { useEffect, useState, useMemo } from "react";
import BodyHeader from "../../components/customComp/BodyHeader";
import SitesBody from "./sites/SitesBody";
import PopUpWindow from "../../components/popups/PopUpWindow";
import formData from "../../components/forms/formsData.json";
import { LoadingComponent } from "../../components/input/LoadingComponent";
import { object, string } from "yup";
import Constants from "../../utils/constants";

const SiteListComp = (props) => {
  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [sitesData, setSitesData] = useState([]);
  const [siteEditEntry, setSiteEditEntry] = useState({});
  const [editForm] = useState(formData["add_site"]);
  const [infoForm] = useState(formData["site_info"]);
  const [filtersObj, setFiltersObj] = useState({});
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const validateSchema = object().shape({
    Site_name: string().required("This field is required"),
    Site_address: string().required("This field is required"),
    Site_country: string().required("This field is required"),
    Site_state: string().required("This field is required"),
    Site_city: string().required("This field is required"),
    Site_postcode: string().required("This field is required"),
    Site_contact_firstname: string().required("This field is required"),
    Site_contact_lastname: string().required("This field is required"),
    Site_contact_phone: string().required("This field is required"),
    Site_contact_email: string()
      .required("This field is a required field")
      .matches(Constants.emailRegExp, "Invalid Email"),
  });

  useEffect(() => {
    props.restApi.getAllSites(filtersObj).then((p) => {
      if (p && p.data) setSitesData(p.data);
      setIsLoading(false);
    });
  }, [isLoading, filtersObj]);

  const searchMatches = useMemo(() => {
    if (sitesData.length > 0 && searchInput) {
      const lower = searchInput.toLowerCase();
      return sitesData.filter((site) => {
        const name = site.Site_name || "";
        const address = site.Site_address || "";
        const contact = `${site.Site_contact_firstname || ""} ${
          site.Site_contact_lastname || ""
        }`;
        return (
          name.toLowerCase().includes(lower) ||
          address.toLowerCase().includes(lower) ||
          contact.toLowerCase().includes(lower)
        );
      });
    }
    return [];
  }, [sitesData, searchInput]);

  const displayedSites = sitesData;

  const handleOpenPopup = () => {
    document.body.style.overflow = "hidden";
    setIsPopUpOpen(true);
    setSearchInput("");
  };

  const handleClosePopUp = () => {
    document.body.style.overflow = "auto";
    setIsPopUpOpen(false);
    setIsEditMode(false);
    setSiteEditEntry({});
  };

  const handlePostApi = async (entry) => {
    const response = isEditMode
      ? await props.restApi.updateSite(siteEditEntry.Site_index, entry)
      : await props.restApi.addSite(entry);
    if (response && response.data) {
      handleClosePopUp();
      setIsLoading(true);
    }
  };

  const handleEditMode = (index) => {
    setIsEditMode(true);
    setSiteEditEntry(sitesData[index]);
    handleOpenPopup();
  };

  const handleOpenSiteInfo = (index) => {
    setIsEditMode(false);
    setSiteEditEntry(sitesData[index]);
    setIsInfoOpen(true);
  };

  // In SiteListComp, update the handleDropDownChange function:

  const handleDropDownChange = async (siteId, enabled) => {
    console.log("Updating site:", siteId, "to enabled:", enabled);

    // Find the current site data
    const currentSite = sitesData.find((site) => site.Site_index === siteId);
    if (!currentSite) {
      console.error("Site not found:", siteId);
      return;
    }

    // OPTIMISTIC UPDATE: Update UI immediately
    setSitesData((prevSites) =>
      prevSites.map((site) =>
        site.Site_index === siteId ? { ...site, Enabled: enabled } : site
      )
    );

    try {
      // Send the complete payload that matches the schema requirements
      const payload = {
        Site_name: currentSite.Site_name, // Required field
        Site_pax_id: currentSite.Site_pax_id || null,
        Site_address: currentSite.Site_address || null,
        Site_country: currentSite.Site_country || null,
        Site_state: currentSite.Site_state || null,
        Site_city: currentSite.Site_city || null,
        Site_postcode: currentSite.Site_postcode || null,
        Site_contact_firstname: currentSite.Site_contact_firstname || null,
        Site_contact_lastname: currentSite.Site_contact_lastname || null,
        Site_contact_email: currentSite.Site_contact_email || null,
        Site_contact_phone: currentSite.Site_contact_phone || null,
        Site_title: currentSite.Site_title || null,
        Enabled: enabled,
      };
      const response = await props.restApi.updateSite(siteId, payload);
      if (!response || !response.data) {
        setSitesData((prevSites) =>
          prevSites.map((site) =>
            site.Site_index === siteId
              ? { ...site, Enabled: !enabled } // Revert to previous state
              : site
          )
        );
      }
    } catch (err) {
      // Revert the optimistic update on error
      setSitesData((prevSites) =>
        prevSites.map((site) =>
          site.Site_index === siteId
            ? { ...site, Enabled: !enabled } // Revert to previous state
            : site
        )
      );
    }
  };

  return (
    <div className="w-full px-12 pt-4 pb-16 h-full bg-[#161616]">
      <div className="flex justify-between">
        <BodyHeader
          title="Sites"
          includeButton
          buttonText="Create Sites"
          includeSubtext={false}
          hasSearch
          includePlus
          searchInput={searchInput}
          searchMatches={searchMatches}
          setSearchInput={setSearchInput}
          onButtonClick={handleOpenPopup}
          onSearchItemClick={handleEditMode}
          searchType="site"
        />
      </div>

      {isLoading && <LoadingComponent style={{ marginLeft: "15%" }} />}

      {!isLoading && (
        <div className="mt-6">
          {/* Edit/Create Popup */}
          {isPopUpOpen && (
            <PopUpWindow
              handlePostApi={handlePostApi}
              isPopUpOpen={isPopUpOpen}
              formSections={editForm}
              editEntry={siteEditEntry}
              isEditMode={isEditMode}
              validateSchema={validateSchema}
              editModeText="Update Site"
              popupTitle={isEditMode ? "Edit Site" : "Create Site"}
              submitText={isEditMode ? "Update" : "Create"}
              updateText="Update"
              closePopUp={handleClosePopUp}
            />
          )}

          {/* Info Popup (read-only view) */}
          {isInfoOpen && (
            <PopUpWindow
              isPopUpOpen={isInfoOpen}
              formSections={[]}
              editEntry={siteEditEntry}
              isEditMode={false}
              popupTitle="Site Info"
              submitText=""
              updateText=""
              validateSchema={null}
              handlePostApi={() => {}}
              closePopUp={() => setIsInfoOpen(false)}></PopUpWindow>
          )}

          {/* Sites List */}
          <SitesBody
            sitesData={displayedSites}
            restApi={props.restApi}
            addTab={props.addTab}
            handleDropDownChange={handleDropDownChange}
            onRowClick={handleEditMode}
            onOpenSiteInfo={handleOpenSiteInfo}
          />
        </div>
      )}
    </div>
  );
};

export default SiteListComp;
